import { ReactElement, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Field, Formik, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import apis from '../../libs/services'
import { setSessionItem } from '../../utils/sessionStorage'
import { useAuth } from '../../hooks/useAuth'
import { Button, Input, PageTitle, SkipLink, UniversalSSOButton } from '../../components/atoms'
import logo from '../../assets/images/logo.svg'
import { Modal } from '../../components/molecules'
import { useAuthContext } from '../../contexts/AuthContext'
import { toast } from 'react-toastify'
import useSetFocusById from '../../hooks/useSetFocus'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'
import { isResponseNotFound, isResponseSuccessful } from '../../utils/request'
import { MobileDetectionWrapper, isMobileDevice } from '../../components/molecules/MobileDetectionWrapper/MobileDetectionWrapper'
import { PhoneBlockingDialog } from '../../components/molecules/PhoneBlockingDialog/PhoneBlockingDialog'
import { logger } from '../../utils'
import OrientationWarningModal from '../../components/molecules/OrientationWarningModal/OrientationWarningModal'
import { getCurrentDomain } from '../../utils/domainDetection'
import { shouldShowPasswordLogin } from '../../utils/authMethodDetection'
import config from '../../config/config'
import { SSOProtocol, type SAML2SSOConfig } from '../../config/ssoTypes'
import { SAML2Provider } from '../../components/sso/SAML2Provider'
import { getRedirectPath, getPostLoginDestination, saveRedirectToSession, buildUrlWithRedirect } from '../../utils/deeplink'

export const pageTitleSignUp = 'Sign Up - Check your email'
export const pageTitleLogin = 'Log In or Sign Up'
export const pageTitleLoginSSO = 'Log In'
export const idWelcomePageMainContent = 'welcome-page-main-content'

interface FormValues {
  email: string
}

const initialValues = {
  email: ''
}

const WelcomeFormSchema = Yup.object().shape({
  email: Yup.string()
    .email('Input valid email!')
    .required('This field is required!')
})

export enum FocusableElement {
  InputEmail = 'input-email',
  ButtonSSO = 'button-sso',
  ButtonContinue = 'button-continue',
  LinkContact = 'link-contact',
}

export const stringErrorNoRegisteredUser = 'Not a registered user'
export const stringErrorCheckingUserStatus = 'Error checking user status'

function Welcome (): ReactElement {
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { getAuthenticatedUser } = useAuthContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [setFocusById] = useSetFocusById()
  const keyboardControls = useKeyboardControls()

  // Authentication method state
  const [authMethod, setAuthMethod] = useState<'sso' | 'password'>('password')
  const [domainConfig, setDomainConfig] = useState<any>(null)

  // New state for orientation warning modal
  const [showOrientationModal, setShowOrientationModal] = useState(false)
  const [pendingFormValues, setPendingFormValues] = useState<FormValues | null>(null)
  const [orientationSubmitting, setOrientationSubmitting] = useState(false)
  const activationInFlight = useRef(false)
  const [isTablet, setIsTablet] = useState(false)

  const [isMobile, setIsMobile] = useState(false)
  const [showPhoneBlocking, setShowPhoneBlocking] = useState(false)

  // Check for domain-based SSO configuration and URL parameters
  useEffect(() => {
    const domain = getCurrentDomain()
    const config_ = config.getDomainSSOConfiguration(domain)
    const isSSOEnabledForDomain = config.isSSOEnabledForDomain(domain)
    const defaultMethod = config.getDefaultAuthMethod(domain)

    setDomainConfig(config_)

    if (!isSSOEnabledForDomain) {
      setAuthMethod('password')
      return
    }

    // Determine what to show based on domain config and URL parameters
    const showPassword = shouldShowPasswordLogin(defaultMethod)
    setAuthMethod(showPassword ? 'password' : 'sso')

    // Show success message if SSO login was successful
    const urlParams = new URLSearchParams(window.location.search)
    const ssoSuccess = urlParams.get('sso_success')
    if (ssoSuccess === 'true') {
      toast.success('SSO authentication completed successfully!', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 5000
      })
    }
  }, [])

  // Check to see if a user is authenticated.  If so, automatically
  // bring them into the app.
  useEffect(() => {
    const checkIsUserAuthenticated = async (): Promise<void> => {
      const user = await getAuthenticatedUser()
      if (
        user !== null &&
        user !== undefined &&
        userProfile !== null &&
        userProfile !== undefined &&
        user.signInDetails?.loginId === userProfile?.email
      ) {
        if (userProfile?.role.slug === 'admin') {
          navigate('/dashboard/all-assigned-students')
        } else if (userProfile != null) {
          navigate(getPostLoginDestination())
        }
      }
    }
    void checkIsUserAuthenticated()

    setFocusById(FocusableElement.InputEmail)
  }, [getAuthenticatedUser, setFocusById, navigate, userProfile])

  // Detect if the current device is a tablet or mobile phone
  useEffect(() => {
    const checkIfTablet = (): boolean => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      const isSmallWindowSize = window.innerWidth < 1024 || window.innerHeight < 768
      const isSmallScreen = window.screen.width < 768 || window.screen.height < 768

      // Tablet detection logic matching MobileDetectionWrapper
      if ((hasTouch && !isSmallScreen) || (hasTouch && isSmallWindowSize && !isSmallScreen)) {
        return true
      }
      return false
    }

    setIsTablet(checkIfTablet())
    setIsMobile(isMobileDevice())

    const handleResize = (): void => {
      setIsTablet(checkIfTablet())
      setIsMobile(isMobileDevice())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Persist redirect to sessionStorage so it survives SSO/SAML browser redirects
  useEffect(() => {
    const redirect = getRedirectPath()
    if (redirect) saveRedirectToSession(redirect)
  }, [])

  const showErrorToUser = (actions: any, errorMessage: string): void => {
    toast.error(errorMessage, { position: toast.POSITION.TOP_CENTER })
    actions.setSubmitting(false)
    actions.setFieldError(
      'email',
      'Something went wrong!'
    )
  }

  const handleSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>): Promise<void> => {
    if (activationInFlight.current) return

    // If it's a tablet device, show the orientation modal first
    if (isTablet) {
      setPendingFormValues(values)
      setShowOrientationModal(true)
      actions.setSubmitting(false)
      return
    }

    activationInFlight.current = true
    try {
      const res = await apis.checkUserStatus(values.email, true)
      if (res === null || res === undefined) {
        showErrorToUser(actions, stringErrorCheckingUserStatus)
      } else if (res.data?.data?.activation_status === false) {
        const response = await apis.beginUserActivation(values.email)
        if (isResponseSuccessful(response)) {
          // On mobile (except allowed survey links): show phone blocking instead of signup modal
          if (isMobile && !res.data?.data?.pending_survey) {
            setShowPhoneBlocking(true)
          } else {
            openModal()
          }
        }
      } else if (isResponseSuccessful(res)) {
        // On mobile (except allowed survey links): send reminder email and show phone blocking
        if (isMobile && !res.data?.data?.pending_survey) {
          try {
            await apis.sendPhoneLoginReminder(values.email)
          } catch (err: any) {
            logger.error('sendPhoneLoginReminder failed:')
            logger.error(err)
          }
          setShowPhoneBlocking(true)
        } else {
          setSessionItem('email', values.email)
          navigate(buildUrlWithRedirect('/login', getRedirectPath()))
        }
      } else if (isResponseNotFound(res)) {
        actions.setErrors({ email: stringErrorNoRegisteredUser })
      } else {
        showErrorToUser(actions, stringErrorCheckingUserStatus)
      }
    } catch (err: any) {
      showErrorToUser(actions, err.message)
    } finally {
      activationInFlight.current = false
      actions.setSubmitting(false)
    }
  }

  // Handler for when user accepts orientation warning
  const handleOrientationAccept = async (): Promise<void> => {
    if (activationInFlight.current) return

    if (pendingFormValues != null) {
      activationInFlight.current = true
      setOrientationSubmitting(true)
      try {
        const res = await apis.checkUserStatus(pendingFormValues.email, true)
        if (res === null || res === undefined) {
          toast.error(stringErrorCheckingUserStatus, { position: toast.POSITION.TOP_CENTER })
        } else if (res.data?.data?.activation_status === false) {
          const response = await apis.beginUserActivation(pendingFormValues.email)
          if (isResponseSuccessful(response)) {
            if (isMobile && !res.data?.data?.pending_survey) {
              setShowPhoneBlocking(true)
            } else {
              openModal()
            }
          }
        } else if (isResponseSuccessful(res)) {
          if (isMobile && !res.data?.data?.pending_survey) {
            try {
              await apis.sendPhoneLoginReminder(pendingFormValues.email)
            } catch (err: any) {
              logger.error('sendPhoneLoginReminder failed:')
              logger.error(err)
            }
            setShowPhoneBlocking(true)
          } else {
            setSessionItem('email', pendingFormValues.email)
            navigate(buildUrlWithRedirect('/login', getRedirectPath()))
          }
        } else if (isResponseNotFound(res)) {
          toast.error(stringErrorNoRegisteredUser, { position: toast.POSITION.TOP_CENTER })
        } else {
          toast.error(stringErrorCheckingUserStatus, { position: toast.POSITION.TOP_CENTER })
        }
      } catch (err: any) {
        toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
      } finally {
        activationInFlight.current = false
        setOrientationSubmitting(false)
        setShowOrientationModal(false)
        setPendingFormValues(null)
      }
    }
  }

  const openModal = (): void => {
    setIsModalOpen(true)
  }

  const renderSignUpModalContent = (values: FormValues): ReactElement => (
    <div className='flex flex-col items-center h-full text-center px-4 py-6'>
      <div className='w-full mb-4 md:mb-8 flex-shrink-0'>
        <PageTitle title={pageTitleSignUp} addScreenReaderH1={false} />
        <h1 className='font-bold capitalize text-[20px] md:text-[26px] text-center' aria-hidden>
          Sign Up
        </h1>
      </div>
      <div className='flex-1 flex flex-col items-center justify-center max-w-[430px] mx-auto min-h-0'>
        <p className='font-bold text-[28px] md:text-[38px] leading-tight mb-3 md:mb-6'>
          Check your email
        </p>
        <p className='text-[18px] md:text-[26px] w-full max-w-[430px] leading-normal md:leading-[3rem] font-thin text-center'>
          Click the button in the email sent to
          <span className='sr-only'> </span>
          <br />
          <span className='text-dopage-darkGreen'>{values.email}</span>
          <br />
          <span className='sr-only'> </span>
          to complete your onboarding.
        </p>
      </div>
      <div className='w-full flex-shrink-0 mt-6 md:mt-auto'>
        <div className='max-w-[440px] mx-auto w-full text-center'>
          <Button
            className='invisible font-primary bg-dopage-primary w-full h-[86px] diamond-btn font-bold mt-[12px] md:mt-[40px] flex justify-center items-center'
          >
            <span className='text-[26px] text-neutral-090'>
              Resend Email
            </span>
          </Button>
          <div className='mt-4 md:mt-8 mb-4 md:mb-8'>
            <ContactLinks
              id={FocusableElement.LinkContact}
            />
          </div>
        </div>
      </div>
    </div>
  )

  const renderLoginModalContent = (formikProps: FormikProps<FormValues>): ReactElement => {
    const { errors, touched, isSubmitting, status } = formikProps

    return (
      <div className='flex flex-col items-center h-full'>
        {/* Top section with equal spacing */}
        <div className='w-full pt-12'>
          <p className='font-bold text-[20px] md:text-[26px] text-dopage-lightGreen text-center' aria-hidden>
            {authMethod === 'sso' ? pageTitleLoginSSO : pageTitleLogin}
          </p>
        </div>

        {/* Middle section - centered vertically */}
        <div className='flex-1 flex flex-col items-center justify-center w-full md:min-w-[440px]'>
          <div className='text-center'>
            <p className='font-bold text-[26px] md:text-[38px] leading-none text-dopage-lightGreen mb-[26px]'>
              Welcome to DopaGE
            </p>
            {authMethod === 'sso' ? (
              <div>
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonSSO)}
                  highlightEffect={HighlightEffect.ButtonHexOutline}
                  data-testid={`highlight-${FocusableElement.ButtonSSO}`}
                >
                  <KeyboardControl>
                    {domainConfig?.ssoConfig?.protocol === SSOProtocol.SAML2 ? (
                      <SAML2Provider config={domainConfig.ssoConfig as SAML2SSOConfig}>
                        <UniversalSSOButton
                          id={FocusableElement.ButtonSSO}
                          data-testid={FocusableElement.ButtonSSO}
                          disabled={isSubmitting}
                        />
                      </SAML2Provider>
                    ) : (
                      <UniversalSSOButton
                        id={FocusableElement.ButtonSSO}
                        data-testid={FocusableElement.ButtonSSO}
                        disabled={isSubmitting}
                      />
                    )}
                  </KeyboardControl>
                </ControlHighlight>
              </div>
            ) : (
              <Form>
                <label aria-label='email'>
                  <Field
                    name='email' type='email'
                  >
                    {({
                      field
                    }: any) => (
                      <div>
                        <ControlHighlight
                          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.InputEmail)}
                          highlightEffect={HighlightEffect.InputOutline}
                          data-testid={`highlight-${FocusableElement.InputEmail}`}
                          activeClassName='mb-[12px]'
                        >
                          <KeyboardControl>
                            <Input
                              id={FocusableElement.InputEmail}
                              data-testid={FocusableElement.InputEmail}
                              className='w-full py-[20px] px-[18px] text-[20px] md:py-[30px] md:px-[27px] md:text-[26px]'
                              placeholder='your@email.com'
                              {...field}
                            />
                          </KeyboardControl>
                        </ControlHighlight>
                      </div>
                    )}
                  </Field>
                </label>
                {errors.email != null && errors.email !== '' && touched.email === true
                  ? (
                    <div
                      className='text-[#ff0000] pt-1'
                      aria-label={`Error message: ${errors.email}`}
                    >
                      {errors.email}
                    </div>
                    )
                  : null}
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonContinue)}
                  highlightEffect={HighlightEffect.ButtonHexOutline}
                  data-testid={`highlight-${FocusableElement.ButtonContinue}`}
                >
                  <KeyboardControl>
                    <Button
                      className='h-[64px] md:h-[86px] w-full diamond-btn font-bold mt-[28px] flex justify-center items-center'
                      data-testid={FocusableElement.ButtonContinue}
                      disabled={isSubmitting}
                      id={FocusableElement.ButtonContinue}
                      loading={isSubmitting}
                      type='submit'
                    >
                      <span className='text-black text-[20px] md:text-[26px]'>
                        Continue
                      </span>
                    </Button>
                  </KeyboardControl>
                </ControlHighlight>
              </Form>
            )}
            <div className='text-[#ff0000] pt-1'>{status}</div>

          </div>
        </div>

        {/* Bottom section with equal spacing to match top */}
        <div className='w-full pb-12 text-center'>
          <ContactLinks
            id={FocusableElement.LinkContact}
          />
        </div>
      </div>
    )
  }

  // Render orientation warning modal
  const renderOrientationWarningModal = (): ReactElement => (
    <Modal>
      <OrientationWarningModal
        acceptDisabled={orientationSubmitting}
        onAccept={() => { void handleOrientationAccept() }}
      />
    </Modal>
  )

  if (showPhoneBlocking) {
    return <PhoneBlockingDialog contactLinkId={FocusableElement.LinkContact} />
  }

  return (
    <MobileDetectionWrapper disabled>
      <div className='bg-black min-h-[100dvh] font-primary flex flex-col'>
        <SkipLink targetId={idWelcomePageMainContent} />
        <header>
          <div className='p-4 md:p-0 md:absolute md:left-[40px] md:top-[40px]'>
            <img src={logo} className='w-[138px]' alt='DopaGE logo' />
          </div>

        </header>
        <main
          className='flex-1 flex items-center justify-center px-4 md:px-0'
          id={idWelcomePageMainContent}
          data-testid={idWelcomePageMainContent}
          tabIndex={-1}
        >
          <PageTitle
            title={isModalOpen
              ? pageTitleSignUp
              : (showOrientationModal
                  ? 'Device Orientation'
                  : (authMethod === 'sso' ? pageTitleLoginSSO : pageTitleLogin))}
            addScreenReaderH1={!showOrientationModal}
          />
          <Formik
            initialValues={initialValues}
            validationSchema={WelcomeFormSchema}
            onSubmit={handleSubmit}
          >
            {(formikProps: FormikProps<FormValues>) => (
              <>
                {showOrientationModal
                  ? (
                      renderOrientationWarningModal()
                    )
                  : isModalOpen
                    ? (
                      <Modal tall>
                        {renderSignUpModalContent(formikProps.values)}
                      </Modal>
                      )
                    : (
                      <Modal>
                        {renderLoginModalContent(formikProps)}
                      </Modal>
                      )}
              </>
            )}
          </Formik>
        </main>

        <footer className='sr-only' />
      </div>
    </MobileDetectionWrapper>
  )
}

export default Welcome
